"use client";

import Link from "next/link";
import Image from "next/image";
import logo from "@/public/logo.svg";
import { User } from "lucide-react";
import { useState, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import useCommonStore from "@/store/commonStore";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import useCartStore from "@/store/cartStore";
import useWishListStore from "@/store/wishListStore";
import { toast } from "@/hooks/use-toast";
import Loader from "@/app/loading";
import { GTMLanguageSwitch } from "@/lib/analytics";

const MegaMenu = dynamic(() => import("./MegaMenu"));
const MobileSearchBar = dynamic(() => import("./MobileSearchBar"), { ssr: false });

export function Navigation() {
	const pathname = usePathname();
	const router = useRouter()
	const [hovered, setHovered] = useState<null | "brand" | "accessories" | "store">(null);
	const { cart } = useCartStore()
	const { wishList } = useWishListStore()
	const { language, updateUserLanguageAndCurrency } = useCommonStore()
	const [loading, setLoading] = useState(false)
	const { brands, fetchHomeScreen, access, translations } = useCommonStore()
	const [isShowSearch, setShowSearch] = useState(false)



	useEffect(() => {
		fetchComp()
		setHovered(null)
	}, [])
	const fetchComp = async () => {
		setLoading(true)
		await fetchHomeScreen()
		setLoading(false)
	}
	const languageToggle = async (code: string) => {
		setLoading(true)
		let language_to = language?.code === "EN" ? "AR" : "EN"

		GTMLanguageSwitch(language?.code, language_to)
		const result = await updateUserLanguageAndCurrency(code, code === "AR" ? "AED" : "AED")
		setLoading(false)
		if (result?.status) {
			window.location.reload()
		} else {
			toast({
				title: "Error",
				description: result?.message || result?.error,
				variant: "destructive"
			})
		}
	}
	// Hide header on login route
	if (pathname === "/login") return null;
	if (loading) {
		return (
			<Loader />
		)
	}

	// close any open megamenu when the route changes (client navigation)
	// useEffect(() => {
	// 	setHovered(null)
	// }, [pathname])

	return (
		<>
			<header className="bg-white shadow-md h-[80px] w-full sticky top-0 z-50 py-2.5 hidden md:block">
				<div className="max-w-7xl mx-auto flex items-center justify-between relative">
					{/* Left: Menu */}
					<nav className="flex-1 flex">
						<ul className="flex gap-8 font-normal relative">
							<li className="flex items-center gap-2">
								{pathname === "/" && (
									<span className="w-2.5 h-2.5 rounded-full bg-chart-4 inline-block" />
								)}
								<Link
									href="/"
									className={`text-black  ${pathname === "/" ? "text-black font-bold" : ""
										}`}
									aria-current={pathname === "/" ? "page" : undefined}
								>
									{translations["home"] || "Home"}
								</Link>
							</li>
							{brands.length > 0 && (<li
								className="relative"
								onMouseEnter={() => setHovered("brand")}
								onMouseLeave={() => setHovered(null)}
							>
								{pathname === "/brand" && (
									<span className="w-2.5 h-2.5 rounded-full bg-yellow-500 mr-2 rtl:ml-2 inline-block" />
								)}
								<Link
									href="/products"
									// onClick={()=>router.push('/products')}
									className={`text-black font-normal ${hovered === "brand" || pathname.startsWith("/brand")
										? "text-black font-bold"
										: ""
										}`}
									aria-current={pathname.startsWith("/brand") ? "page" : undefined}
								>
									{translations["brnd"]}
								</Link>
								{hovered === "brand" && <MegaMenu menu={brands} columns={3} onClose={() => setHovered(null)} />}
							</li>)}
							{access.length > 0 && (<li
								className="relative"
								onMouseEnter={() => setHovered("accessories")}
								onMouseLeave={() => setHovered(null)}>
								{pathname === "/accessories" && (
									<span className="w-2.5 h-2.5 rounded-full bg-yellow-500 mr-2 rtl:ml-2 inline-block" />
								)}
								<Link
									href="/products?f_cat_id=PDC22"
									className={`text-black ${hovered === "accessories" || pathname.startsWith("/accessories") ? "text-black font-bold" : ""
										}`}
									aria-current={pathname.startsWith("/accessories") ? "page" : undefined}
								>
									{translations["accessories"]}
								</Link>
								{hovered === "accessories" && <MegaMenu menu={access} columns={3} onClose={() => setHovered(null)} />}
							</li>)}
							<li
								className="relative"
							// onMouseEnter={() => setHovered("store")}
							// onMouseLeave={() => setHovered(null)}
							>
								{pathname.startsWith("/products") && (
									<span className="w-2.5 h-2.5 rounded-full bg-yellow-500 mr-2 rtl:ml-2 inline-block" />
								)}
								<Link
									href="/products"
									className={`text-black  ${hovered === "store" || pathname.startsWith("/products")
										? "text-black font-bold"
										: ""
										}`}
									aria-current={pathname.startsWith("/products") ? "page" : undefined}
								>
									{translations["store"] || "Store"}
								</Link>
								{/* {hovered === "store" && <MegaMenu menu={STORE_MENU} columns={4} />} */}
							</li>
							<li>
								<Link
									href="https://blog.curlified.com/"
									target="_blank"
									className="text-black "
								>
									{translations["blogs"] || "Blogs"}
								</Link>
							</li>
						</ul>
					</nav>

					{/* Center: Logo and tagline */}
					<div className="flex flex-shrink-0">
						<Link href="/" rel="stylesheet">
							<Image
								src={logo}
								alt="Curlified Logo"
								width={180}
								height={60}
							/>
						</Link>
					</div>

					{/* Right: Language Switcher and Icons */}
					<div className="flex-1 flex items-center justify-end gap-10 text-black">
						{/* Language Switcher */}
						<Select value={language?.code} onValueChange={(a) => languageToggle(a)}>
							<SelectTrigger className="w-24 cursor-pointer" aria-label="Select language">
								<SelectValue placeholder="Language" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="EN">English</SelectItem>
								<SelectItem value="AR">العربية</SelectItem>
							</SelectContent>
						</Select>
						<div className="flex items-center gap-10">
							{/* <SearchComponent /> */}
							<button onClick={() => setShowSearch(true)}>
								<img src={`${process.env.NEXT_PUBLIC_ICON_URL}9b199114-f6d9-44aa-a456-7927f715a975.svg`} alt="Icon" className="w-6 h-6" />
							</button>
							<Link
								href="/mycart"
								aria-label="Cart"
								className="relative "
							>
								<img src={`${process.env.NEXT_PUBLIC_ICON_URL}3c17c865-3652-49af-9125-defed82e01db.svg`} alt="cart icon" className="w-8 h-8" />
								{cart?.length > 0 && <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 font-bold">
									{cart?.length}
								</span>}
							</Link>
							<Link
								href="/profile?tab=wishlist"
								aria-label="Wishlist"
								className="relative"
							>
								<img
									src={`${process.env.NEXT_PUBLIC_ICON_URL}9b871b36-dcdc-4f70-a107-7e9199484a38.svg`}
									alt="wishlist"
									className="w-6 h-6"
								/>
								{wishList?.length > 0 && <span className="absolute top-0 -right-0 bg-red-500 text-white text-xs rounded-full px-1 py-1 font-bold">
									{/* {wishList?.length} */}
								</span>}
							</Link>
							<Link
								href="/profile?tab=profile"
								aria-label="Account"
								className=""
							>
								<User size={24} />
							</Link>
						</div>
					</div>
				</div>
			</header>
			{isShowSearch && (
				<MobileSearchBar showSearch={isShowSearch} onClose={() => setShowSearch(false)} />
			)}
		</>
	)
}
